import React, { useEffect, useMemo, useState } from "react";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams
} from "react-router-dom";
import LayoutTabs from "@/components/controls/LayoutTabs";
import { ChevronRight, Tasks } from "@dreamhub/designsystem/icons";
import Activities from "@/components/layouts/Activities/Activities";
import TranscriptProcessingBanner from "@/components/layouts/Activities/components/TranscriptProcessingBanner";
import { ClosedRecordActivityState } from "@/components/layouts/Activities/components/ActivityClosedRecordEmptyState";
import {
  useRecordTranscriptProcessing,
  useTranscriptBannerDismissal
} from "@/entities/activities/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import NotFoundPage from "@/components/layouts/NotFoundPage";
import { getEnumTKey } from "@/utils/strings";
import PeopleTab from "@/components/layouts/PeopleTab/PeopleTab";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import SalesExplorationMeeting from "@/components/layouts/SalesExplorationMeeting";
import { LeadStatus } from "@/pages/leads/types";
import OptionsDropdownMenu, {
  DetailedViewOptions
} from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import Commercials from "@/pages/deals/components/commercials/Commercials";
import TasksDrawer from "@/components/layouts/TasksDrawer/TasksDrawer";
import { cn } from "@/utils/utils";
import { useAccountSettingByKey } from "@/entities/settings/hooks";
import { useHasAdditionalFields } from "@/pages/settings/hooks";
import { StageBadge } from "@/components/ui/stage-badge";
import { DealData, DealStage } from "@/pages/deals/types";
import { EntityData } from "@/services/api/types";
import SingleInput from "@/components/controls/SingleInput";
import { PersonData } from "@/entities/people/types";
import { getFullName } from "@/entities/people/helpers";
import { PartnerContactData } from "@/pages/partners/contacts/types";
import { getPartnerContactFullName } from "@/pages/partners/contacts/helpers";
import { IconButton } from "@/components/ui/icon-button";
import { forEach, isEmpty } from "lodash";
import IconContainer from "@/components/ui/icon-container";
import Spinner from "@/components/ui/spinner";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import AdditionalInfoTab from "@/components/layouts/AdditionalInfoTab";
import PartnerLeadsTab from "@/pages/partners/components/PartnerDetailedView/PartnerLeadsTab";
import PartnerDealsTab from "@/pages/partners/components/PartnerDetailedView/PartnerDealsTab";
import TabBadge from "@/components/ui/tab-badge";

export enum TabOption {
  OVERVIEW = 1,
  STAGE_WORKFLOWS,
  SALES_EXPLORATION_MEETING,
  COMMERCIALS,
  ACTIVITIES,
  PEOPLE,
  ADDITIONAL_INFO,
  LEADS,
  DEALS
}

export const TAB_SEARCH_PARAM = "tab";

export const getTabSlug = (tab: TabOption): string =>
  TabOption[tab].toLowerCase();

const tabSlugToValue = (slug: string): string | undefined => {
  const value = TabOption[slug.toUpperCase() as keyof typeof TabOption];
  return typeof value === "number" ? String(value) : undefined;
};

const defaultTabs = [
  TabOption.OVERVIEW.toString(),
  TabOption.STAGE_WORKFLOWS.toString(),
  TabOption.ACTIVITIES.toString()
];

const getEntityTypeToTabOptions = (): Partial<
  Record<EntityType, string[]>
> => ({
  [EntityType.PERSON]: [
    TabOption.OVERVIEW.toString(),
    TabOption.ACTIVITIES.toString()
  ],
  [EntityType.COMPANY]: [
    TabOption.OVERVIEW.toString(),
    TabOption.COMMERCIALS.toString(),
    TabOption.ACTIVITIES.toString(),
    TabOption.PEOPLE.toString()
  ],
  [EntityType.DEAL]: [
    TabOption.OVERVIEW.toString(),
    TabOption.STAGE_WORKFLOWS.toString(),
    TabOption.COMMERCIALS.toString(),
    TabOption.ACTIVITIES.toString(),
    TabOption.PEOPLE.toString()
  ],
  [EntityType.LEAD]: [
    TabOption.OVERVIEW.toString(),
    // Commented out until ready
    // TabOption.SALES_EXPLORATION_MEETING.toString(),
    TabOption.ACTIVITIES.toString()
  ],
  [EntityType.PARTNER]: [
    TabOption.OVERVIEW.toString(),
    TabOption.LEADS.toString(),
    TabOption.DEALS.toString(),
    TabOption.ACTIVITIES.toString(),
    TabOption.PEOPLE.toString()
  ],
  [EntityType.PARTNER_CONTACT]: [
    TabOption.OVERVIEW.toString(),
    TabOption.ACTIVITIES.toString()
  ]
});

const getAvailableTabs = (
  entityType: EntityType,
  leadStatus: LeadStatus | undefined,
  showAdditionalInfoTab: boolean
): string[] => {
  let tabs = getEntityTypeToTabOptions()?.[entityType] || defaultTabs;

  if (entityType === EntityType.LEAD && leadStatus === LeadStatus.CONFIRM_ICP) {
    tabs = tabs.filter(
      (tab) => tab !== TabOption.SALES_EXPLORATION_MEETING.toString()
    );
  }

  if (showAdditionalInfoTab) {
    tabs = [...tabs, TabOption.ADDITIONAL_INFO.toString()];
  }

  return tabs;
};

interface Props {
  entityData?: EntityData;
  entityType: EntityType;
  isError?: boolean;
  onUpdateField: (
    field: string,
    newValue: string | number,
    additionalFields?: {
      [fieldName: string]: string | number;
    },
    successToastText?: string,
    onSuccessFn?: () => void
  ) => void;
  isTasksDrawerOpen: boolean;
  toggleTasksDrawer: () => void;
  pageLink?: string;
  topRightComponent?: JSX.Element;
  options?: DetailedViewOptions;
  topOptions?: DetailedViewOptions;
  topFields?: JSX.Element;
  tabIcons?: { [key: string]: JSX.Element };
  tabBadges?: { [key: string]: number };
  overviewContent?: JSX.Element;
  stageWorkflowsContent?: JSX.Element;
  commercialsTopContent?: React.ReactNode;
  activeTabOverride?: string;
  onTabChange?: (tab: string) => void;
  leadStatus?: LeadStatus;
  currentStage?: DealStage;
  onDealUpdate?: (updatedFields: Partial<DealData>) => void;
  refetchEntityWithAdditionals?: () => void;
  /** Closes the Activities composer when the record was converted/inactivated. */
  closedRecordState?: ClosedRecordActivityState;
}

const DetailedView: React.FC<Props> = ({
  entityData,
  entityType,
  isError = false,
  onUpdateField,
  isTasksDrawerOpen,
  toggleTasksDrawer,
  pageLink,
  topRightComponent,
  options,
  topOptions,
  topFields,
  tabIcons,
  tabBadges,
  overviewContent,
  stageWorkflowsContent,
  commercialsTopContent,
  activeTabOverride,
  onTabChange,
  leadStatus,
  currentStage,
  onDealUpdate,
  refetchEntityWithAdditionals,
  closedRecordState
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { data: additionalInfoTabSetting } = useAccountSettingByKey(
    "additional_info_tab_enabled"
  );
  const hasAdditionalFields = useHasAdditionalFields(entityType);
  const showAdditionalInfoTab =
    additionalInfoTabSetting?.value === true && hasAdditionalFields;

  const urlTabSlug = searchParams.get(TAB_SEARCH_PARAM);
  const urlTab = urlTabSlug ? tabSlugToValue(urlTabSlug) : undefined;

  const [tabValue, setTabValue] = useState(() => {
    const availableTabs = getAvailableTabs(
      entityType,
      leadStatus,
      showAdditionalInfoTab
    );
    return urlTab && availableTabs.includes(urlTab)
      ? urlTab
      : TabOption.OVERVIEW.toString();
  });

  const availableTabs = useMemo(
    () => getAvailableTabs(entityType, leadStatus, showAdditionalInfoTab),
    [entityType, leadStatus, showAdditionalInfoTab]
  );

  const entityId = entityData?.id;
  const pageName = entityTypeToEntityName[entityType];

  // Reuses the existing transcript-processing feature flag so the record-level
  // banner appears and clears in lockstep with the per-activity indication.
  const { enabled: transcriptBannerEnabled } = useFeatureFlag(
    FeatureFlags.CRM_8124_TRANSCRIPT_PROCESSING
  );
  const { isProcessing: hasProcessingTranscript, processingKey } =
    useRecordTranscriptProcessing(
      pageName,
      entityId || "",
      transcriptBannerEnabled
    );
  const {
    isDismissed: isTranscriptBannerDismissed,
    dismiss: dismissTranscriptBanner
  } = useTranscriptBannerDismissal(entityId || "", processingKey);

  useEffect(() => {
    if (isTasksDrawerOpen) {
      document.body.classList.add("no-scroll");
    } else {
      document.body.classList.remove("no-scroll");
    }
    return () => {
      document.body.classList.remove("no-scroll");
    };
  }, [isTasksDrawerOpen]);

  useEffect(() => {
    if (activeTabOverride && activeTabOverride !== tabValue) {
      setTabValue(activeTabOverride);
      onTabChange?.(activeTabOverride);
    }
  }, [activeTabOverride, tabValue, onTabChange]);

  useEffect(() => {
    if (!urlTab || activeTabOverride) return;

    if (availableTabs.includes(urlTab) && urlTab !== tabValue) {
      setTabValue(urlTab);
    }
  }, [urlTab, activeTabOverride, availableTabs, tabValue]);

  // Show 404 when API returned an error (e.g., 404 not found)
  if (isError) {
    return <NotFoundPage data-test="detailed-view-404" />;
  }

  // Show loader while data is being fetched
  if (!entityData) {
    return (
      <div className="flex items-center justify-center h-full w-full min-h-[400px]">
        <Spinner size="lg" />{" "}
      </div>
    );
  }

  const getCompanyId: () => string = () => {
    if (entityType === EntityType.COMPANY) {
      return entityId;
    }
    if (!isEmpty(entityData?.companyId)) {
      return entityData?.companyId;
    }
    return "";
  };

  const companyId = getCompanyId();

  const getTabOptions = () => {
    const tabOptions: { [key: string]: string | JSX.Element } = {};
    forEach(availableTabs, (tabString) => {
      const label = t(
        getEnumTKey("detailedView", "tabs", TabOption, Number(tabString))
      );
      const badgeCount = tabBadges?.[tabString];
      tabOptions[tabString] =
        badgeCount !== undefined ? (
          <TabBadge label={label} count={badgeCount} />
        ) : (
          label
        );
    });
    return tabOptions;
  };

  const renderTabContent = () => {
    switch (tabValue) {
      case TabOption.OVERVIEW.toString():
        return overviewContent ? overviewContent : null;
      case TabOption.STAGE_WORKFLOWS.toString():
        return stageWorkflowsContent ? stageWorkflowsContent : null;
      case TabOption.ACTIVITIES.toString():
        return (
          <Activities
            pageName={pageName}
            entityData={entityData}
            entityType={entityType}
            isTasksDrawerOpen={isTasksDrawerOpen}
            closedRecordState={closedRecordState}
          />
        );
      case TabOption.SALES_EXPLORATION_MEETING.toString():
        return <SalesExplorationMeeting />;
      case TabOption.COMMERCIALS.toString():
        return (
          <div className="flex flex-col w-full gap-6">
            {commercialsTopContent}
            <div className="flex w-full items-center justify-center">
              <Commercials
                entityType={entityType}
                entityData={entityData}
                onDealUpdate={onDealUpdate}
              />
            </div>
          </div>
        );
      case TabOption.PEOPLE.toString():
        return (
          <PeopleTab
            entityId={entityData?.id}
            companyId={companyId}
            dealId={entityType === EntityType.DEAL ? entityId : null}
            partnerId={entityType === EntityType.PARTNER ? entityId : null}
          />
        );
      case TabOption.LEADS.toString():
        return entityType === EntityType.PARTNER && entityId ? (
          <PartnerLeadsTab key={entityId} partnerId={entityId} />
        ) : null;
      case TabOption.DEALS.toString():
        return entityType === EntityType.PARTNER && entityId ? (
          <PartnerDealsTab key={entityId} partnerId={entityId} />
        ) : null;
      case TabOption.ADDITIONAL_INFO.toString():
        return (
          <AdditionalInfoTab
            entityData={entityData}
            entityType={entityType}
            onUpdateField={onUpdateField}
            refetchEntityWithAdditionals={refetchEntityWithAdditionals}
          />
        );
      default:
        return null;
    }
  };

  const showTranscriptBanner =
    transcriptBannerEnabled &&
    hasProcessingTranscript &&
    !isTranscriptBannerDismissed;

  const isTableTab = [
    TabOption.PEOPLE.toString(),
    TabOption.LEADS.toString(),
    TabOption.DEALS.toString()
  ].includes(tabValue);

  return (
    <div
      className={cn(
        "flex flex-col h-full",
        isTableTab &&
          "max-h-[calc(var(--app-content-h)-var(--table-bottom-space))]",
        !isTableTab && isTasksDrawerOpen && "max-h-[var(--app-content-h)]"
      )}
      data-test={`detailed-view-${entityType}`}
    >
      <div
        className="flex flex-col top-0 bg-surface-elevation0 z-10 sticky w-full rounded-t-[20px]"
        data-test={`detailed-view-${entityType}-header`}
      >
        {showTranscriptBanner ? (
          <TranscriptProcessingBanner
            entityType={entityType}
            onDismiss={dismissTranscriptBanner}
          />
        ) : null}
        {/* Record header tucks up over the banner's bottom edge with a rounded,
            top-bordered edge so it reads as a panel sitting in front of the
            banner, with the banner peeking out behind its top corners. */}
        <div
          className={cn(
            "flex flex-col w-full bg-surface-elevation0",
            showTranscriptBanner &&
              "relative z-10 rounded-t-[12px] -mt-3 border-t-[0.5px] border-border-border1"
          )}
        >
          <div className="w-full flex justify-between p-4 pb-2">
            <div className="flex gap-1 items-center">
              <div className="flex gap-1 items-center">
                <div className="flex gap-[10px] items-center">
                  <EntityLogoIconContainer entityType={entityType} />
                  <Link
                    to={pageLink ? pageLink : `/${pageName}`}
                    data-test={`detailed-view-${entityType}-breadcrumb-link`}
                  >
                    <Text variant="lgSubtitleH3" className="p-1">
                      {t(`pages.${pageName}`)}
                    </Text>
                  </Link>
                </div>
                <IconContainer color="secondary" size="5">
                  <ChevronRight />
                </IconContainer>
                <SingleInput
                  fieldName="name"
                  initialValue={
                    entityType === EntityType.PERSON && entityData
                      ? getFullName(entityData as PersonData)
                      : entityType === EntityType.PARTNER_CONTACT && entityData
                        ? getPartnerContactFullName(
                            entityData as PartnerContactData
                          )
                        : entityData?.name
                  }
                  onSave={(newValue: string) => {
                    if (entityType === EntityType.PERSON) {
                      const [firstName, lastName] = newValue.split(" ");
                      onUpdateField("firstName", firstName, { lastName });
                    } else if (entityType === EntityType.PARTNER_CONTACT) {
                      const [firstName = "", ...rest] = newValue
                        .trim()
                        .split(/\s+/)
                        .filter(Boolean);
                      onUpdateField("firstName", firstName, {
                        lastName: rest.join(" ")
                      });
                    } else {
                      onUpdateField("name", newValue);
                    }
                  }}
                  textVariant="lgSubtitleH1"
                  inputVariant="lgSubtitleH1"
                  className="pl-0"
                  width="fit-content"
                  placeholder={t("detailedView.emptyTitle", {
                    entity: t(`detailedView.emptyNameEntities.${pageName}`)
                  })}
                  divClassName="py-0 px-2"
                  data-test={`detailed-view-${entityType}-name-input`}
                />
              </div>
              {entityType === EntityType.DEAL && currentStage && (
                <StageBadge stage={currentStage} data-test="current-stage" />
              )}
            </div>
            <div
              className="flex items-center gap-2"
              data-test={`detailed-view-${entityType}-actions`}
            >
              {topRightComponent}
              {options && (
                <OptionsDropdownMenu
                  options={options}
                  topOptions={topOptions}
                  tPrefix="detailedView.options"
                  iconSize="lg"
                  data-test={`detailed-view-${entityType}-options-menu`}
                />
              )}
            </div>
          </div>
          {topFields}
          <div
            className="flex w-full pr-4 items-center relative border-b-[1px] border-widget"
            data-test={`detailed-view-${entityType}-tabs-container`}
          >
            <div className="flex-grow">
              <LayoutTabs
                options={getTabOptions()}
                order={availableTabs}
                icons={tabIcons}
                defaultValue={tabValue}
                valueSelected={(newTabValue) => {
                  setTabValue(newTabValue);
                  onTabChange?.(newTabValue);

                  const params = new URLSearchParams(location.search);
                  params.set(TAB_SEARCH_PARAM, getTabSlug(Number(newTabValue)));
                  navigate(`${location.pathname}?${params.toString()}`, {
                    replace: true
                  });
                }}
                className="border-none"
                data-test={`detailed-view-${entityType}-tabs`}
              />
            </div>
            <IconButton
              variant="iconNoBg"
              size="lg"
              onClick={toggleTasksDrawer}
              className={cn(isTasksDrawerOpen && "text-primary")}
              iconColor={isTasksDrawerOpen ? "primary" : "secondary"}
              data-test={`detailed-view-${entityType}-tasks-button`}
            >
              <Tasks />
            </IconButton>
          </div>
        </div>
      </div>
      <div
        className={cn(
          "flex w-full h-full",
          (isTableTab || isTasksDrawerOpen) && "min-h-0"
        )}
      >
        <div
          className={cn(
            "w-full p-6 overflow-y-hidden",
            isTableTab && "p-0",
            // tab content owns its scroll so the bounded panel keeps its footer in view
            isTasksDrawerOpen && !isTableTab && "overflow-y-auto"
          )}
          data-test={`detailed-view-${entityType}-tab-content`}
        >
          {renderTabContent()}
        </div>
        {entityData && (
          <TasksDrawer
            isOpen={isTasksDrawerOpen}
            toggleDrawer={toggleTasksDrawer}
            entityData={entityData}
            entityType={entityType}
            data-test={`detailed-view-${entityType}-tasks-drawer`}
          />
        )}
      </div>
    </div>
  );
};

export default DetailedView;
